import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { toast } from "sonner";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { PencilSimple, Trash, Plus, Package, Shield, CurrencyDollar, CalendarCheck, ClockCounterClockwise, Check, X, Certificate as CertIcon, Medal, Student, TrendUp, EnvelopeSimple, PaperPlaneTilt, Warning } from "@phosphor-icons/react";

const empty = { title: "", level: "", price: 19, tagline: "", description: "", image: "", color: "#0ea5e9", featured: false, lessons: [] };

export default function Admin() {
  const { user } = useAuth();
  const [packages, setPackages] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(empty);
  const [editId, setEditId] = useState(null);
  const [deleteId, setDeleteId] = useState(null);
  const [bookings, setBookings] = useState([]);
  const [completions, setCompletions] = useState([]);
  const [emailConfig, setEmailConfig] = useState(null);
  const [testEmail, setTestEmail] = useState("");
  const [sendingTest, setSendingTest] = useState(false);

  const load = () => api.get("/packages").then((r) => setPackages(r.data)).catch(() => {});
  const loadBookings = () => api.get("/admin/bookings").then((r) => setBookings(r.data)).catch(() => {});
  const loadCompletions = () => api.get("/admin/completions").then((r) => setCompletions(r.data)).catch(() => {});
  const loadEmailConfig = () => api.get("/admin/email-config").then((r) => setEmailConfig(r.data)).catch(() => {});
  useEffect(() => { load(); loadBookings(); loadCompletions(); loadEmailConfig(); }, []);

  const sendTestEmail = async () => {
    if (!testEmail) { toast.error("Enter an email address."); return; }
    setSendingTest(true);
    try {
      const r = await api.post("/admin/test-email", { recipient_email: testEmail });
      toast.success(`Test email sent to ${testEmail}!`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to send test email.");
    } finally {
      setSendingTest(false);
    }
  };

  const setBookingStatus = async (id, status) => {
    try {
      await api.put(`/admin/bookings/${id}`, { status });
      toast.success(`Consultation ${status}.`);
      loadBookings();
    } catch {
      toast.error("Could not update status.");
    }
  };

  const openCreate = () => { setForm(empty); setEditId(null); setOpen(true); };
  const openEdit = (p) => {
    setForm({ title: p.title, level: p.level, price: p.price, tagline: p.tagline, description: p.description, image: p.image, color: p.color, featured: p.featured, lessons: [] });
    setEditId(p.id);
    setOpen(true);
  };

  const save = async () => {
    if (!form.title || !form.level) { toast.error("Title and level are required."); return; }
    const payload = { ...form, price: Number(form.price) };
    try {
      if (editId) {
        // preserve existing lessons: fetch current then send back
        const cur = packages.find((p) => p.id === editId);
        payload.lessons = form.lessons.length ? form.lessons : (cur?._lessons || []);
        await api.put(`/admin/packages/${editId}`, payload);
        toast.success("Package updated!");
      } else {
        await api.post("/admin/packages", payload);
        toast.success("Package created!");
      }
      setOpen(false);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Save failed.");
    }
  };

  const doDelete = async () => {
    try {
      await api.delete(`/admin/packages/${deleteId}`);
      toast.success("Package deleted.");
      setDeleteId(null);
      load();
    } catch {
      toast.error("Delete failed.");
    }
  };

  const revenue = packages.reduce((s, p) => s + Number(p.price), 0);
  const pendingCount = bookings.filter((b) => b.status === "pending").length;
  const certCount = completions.filter((c) => c.completed).length;
  const activeLearners = new Set(completions.map((c) => c.user_id)).size;

  useEffect(() => { if (user?.email && !testEmail) setTestEmail(user.email); }, [user, testEmail]);

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 py-12" data-testid="admin-page">
      <div className="flex items-center gap-2 mb-2">
        <Shield size={22} weight="duotone" className="text-brand-grape" />
        <span className="font-body font-semibold text-brand-grape uppercase tracking-[0.2em] text-xs">Admin</span>
      </div>
      <div className="mb-8">
        <h1 className="font-heading font-bold text-4xl text-ink tracking-tight">Admin Dashboard</h1>
        <p className="font-body text-ink-soft mt-1">Signed in as {user?.email}</p>
      </div>

      <Tabs defaultValue="packages" className="w-full">
        <TabsList className="mb-8 rounded-full bg-white sticker p-1 h-auto">
          <TabsTrigger value="packages" className="rounded-full font-semibold data-[state=active]:bg-brand-grape data-[state=active]:text-white px-5 py-2" data-testid="tab-packages">
            <Package size={18} weight="duotone" className="mr-2" /> Packages
          </TabsTrigger>
          <TabsTrigger value="bookings" className="rounded-full font-semibold data-[state=active]:bg-brand-grape data-[state=active]:text-white px-5 py-2" data-testid="tab-bookings">
            <CalendarCheck size={18} weight="duotone" className="mr-2" /> Consultations
            {pendingCount > 0 && <span className="ml-2 grid place-items-center min-w-5 h-5 px-1 rounded-full bg-brand-coral text-white text-xs">{pendingCount}</span>}
          </TabsTrigger>
          <TabsTrigger value="completions" className="rounded-full font-semibold data-[state=active]:bg-brand-grape data-[state=active]:text-white px-5 py-2" data-testid="tab-completions">
            <CertIcon size={18} weight="duotone" className="mr-2" /> Completions
          </TabsTrigger>
        </TabsList>

        <TabsContent value="packages">
          <div className="flex justify-end mb-6">
            <Button onClick={openCreate} className="rounded-full bg-brand-grape hover:bg-brand-grape/90 text-white font-semibold px-6 py-5" data-testid="new-package-btn">
              <Plus size={20} weight="bold" className="mr-2" /> New package
            </Button>
          </div>
          <div className="grid sm:grid-cols-3 gap-5 mb-8">
            {[
              { icon: Package, c: "#0ea5e9", label: "Total packages", value: packages.length },
              { icon: CurrencyDollar, c: "#22c55e", label: "Catalog value", value: `$${revenue.toFixed(0)}` },
              { icon: CalendarCheck, c: "#a855f7", label: "Pending consults", value: pendingCount },
            ].map((s, i) => (
              <div key={i} className="bg-white sticker rounded-3xl p-6 flex items-center gap-4">
                <span className="grid place-items-center w-12 h-12 rounded-2xl" style={{ backgroundColor: `${s.c}20` }}>
                  <s.icon size={26} weight="duotone" style={{ color: s.c }} />
                </span>
                <div>
                  <p className="font-body text-sm text-ink-soft">{s.label}</p>
                  <p className="font-heading font-bold text-2xl text-ink">{s.value}</p>
                </div>
              </div>
            ))}
          </div>

      <div className="bg-white sticker rounded-3xl overflow-hidden">
        <table className="w-full">
          <thead>
            <tr className="border-b border-slate-900/10 font-body text-sm text-ink-soft">
              <th className="text-left p-4">Package</th>
              <th className="text-left p-4 hidden sm:table-cell">Level</th>
              <th className="text-left p-4">Price</th>
              <th className="text-left p-4 hidden sm:table-cell">Lessons</th>
              <th className="text-right p-4">Actions</th>
            </tr>
          </thead>
          <tbody>
            {packages.map((p) => (
              <motion.tr key={p.id} initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="border-b border-slate-900/5 last:border-0" data-testid={`admin-row-${p.slug}`}>
                <td className="p-4">
                  <div className="flex items-center gap-3">
                    {p.image ? (
                      <img src={p.image} alt="" className="w-12 h-12 rounded-xl object-cover" />
                    ) : (
                      <span className="w-12 h-12 rounded-xl grid place-items-center" style={{ backgroundColor: `${p.color}22` }}>
                        <Package size={22} weight="duotone" style={{ color: p.color }} />
                      </span>
                    )}
                    <span className="font-heading font-semibold text-ink">{p.title}</span>
                  </div>
                </td>
                <td className="p-4 hidden sm:table-cell font-body text-ink-soft">{p.level}</td>
                <td className="p-4 font-heading font-bold text-ink">${Number(p.price).toFixed(0)}</td>
                <td className="p-4 hidden sm:table-cell font-body text-ink-soft">{p.lesson_count}</td>
                <td className="p-4">
                  <div className="flex items-center justify-end gap-2">
                    <Button variant="ghost" size="icon" className="rounded-full hover:bg-brand-sky/10 text-brand-sky" onClick={() => openEdit(p)} data-testid={`edit-${p.slug}`}>
                      <PencilSimple size={18} weight="duotone" />
                    </Button>
                    <Button variant="ghost" size="icon" className="rounded-full hover:bg-brand-coral/10 text-brand-coral" onClick={() => setDeleteId(p.id)} data-testid={`delete-${p.slug}`}>
                      <Trash size={18} weight="duotone" />
                    </Button>
                  </div>
                </td>
              </motion.tr>
            ))}
          </tbody>
        </table>
      </div>
        </TabsContent>

        <TabsContent value="bookings">
          <div className="bg-white sticker rounded-3xl overflow-hidden" data-testid="bookings-table">
            {bookings.length === 0 ? (
              <div className="p-12 text-center">
                <ClockCounterClockwise size={40} weight="duotone" className="mx-auto text-brand-grape mb-3" />
                <p className="font-heading font-bold text-xl text-ink">No consultation requests yet</p>
                <p className="font-body text-ink-soft mt-1">New tutor session requests will appear here.</p>
              </div>
            ) : (
              <table className="w-full">
                <thead>
                  <tr className="border-b border-slate-900/10 font-body text-sm text-ink-soft">
                    <th className="text-left p-4">Family</th>
                    <th className="text-left p-4 hidden sm:table-cell">Preferred time</th>
                    <th className="text-left p-4">Status</th>
                    <th className="text-right p-4">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {bookings.map((b) => (
                    <tr key={b.booking_id} className="border-b border-slate-900/5 last:border-0" data-testid={`booking-row-${b.booking_id}`}>
                      <td className="p-4">
                        <p className="font-heading font-semibold text-ink">{b.user_name}</p>
                        <p className="font-body text-sm text-ink-soft">{b.user_email}</p>
                      </td>
                      <td className="p-4 hidden sm:table-cell font-body text-ink">{b.label || "—"}</td>
                      <td className="p-4">
                        <span className={`px-3 py-1 rounded-full text-xs font-bold capitalize ${
                          b.status === "confirmed" ? "bg-brand-grass/15 text-brand-grass"
                          : b.status === "cancelled" ? "bg-brand-coral/15 text-brand-coral"
                          : "bg-brand-sun/40 text-ink"}`} data-testid={`booking-status-${b.booking_id}`}>
                          {b.status}
                        </span>
                      </td>
                      <td className="p-4">
                        <div className="flex items-center justify-end gap-2">
                          <Button variant="ghost" size="icon" className="rounded-full hover:bg-brand-grass/10 text-brand-grass" onClick={() => setBookingStatus(b.booking_id, "confirmed")} data-testid={`confirm-${b.booking_id}`} title="Confirm">
                            <Check size={18} weight="bold" />
                          </Button>
                          <Button variant="ghost" size="icon" className="rounded-full hover:bg-brand-coral/10 text-brand-coral" onClick={() => setBookingStatus(b.booking_id, "cancelled")} data-testid={`cancel-${b.booking_id}`} title="Cancel">
                            <X size={18} weight="bold" />
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </TabsContent>

        <TabsContent value="completions">
          <div className="bg-white sticker rounded-3xl p-6 mb-8" data-testid="email-test-panel">
            <div className="flex items-start gap-4">
              <span className="grid place-items-center w-12 h-12 rounded-2xl bg-brand-sky/15 shrink-0">
                <EnvelopeSimple size={26} weight="duotone" className="text-brand-sky" />
              </span>
              <div className="flex-1">
                <h3 className="font-heading font-bold text-xl text-ink">Email delivery</h3>
                {emailConfig && (
                  emailConfig.configured ? (
                    <p className="font-body text-sm text-ink-soft mt-1">
                      Certificate emails send from <span className="font-semibold text-ink">{emailConfig.sender}</span>. Send yourself a test to confirm delivery.
                    </p>
                  ) : (
                    <p className="font-body text-sm text-brand-coral mt-1 flex items-center gap-1.5">
                      <Warning size={16} weight="fill" /> No Resend API key configured — emails are disabled.
                    </p>
                  )
                )}
                <div className="flex flex-col sm:flex-row gap-3 mt-4">
                  <Input
                    type="email"
                    className="rounded-xl sm:max-w-xs"
                    placeholder="you@example.com"
                    value={testEmail}
                    onChange={(e) => setTestEmail(e.target.value)}
                    disabled={!emailConfig?.configured}
                    data-testid="test-email-input"
                  />
                  <Button
                    onClick={sendTestEmail}
                    disabled={sendingTest || !emailConfig?.configured}
                    className="rounded-full bg-brand-sky hover:bg-brand-skylight text-white font-semibold px-6"
                    data-testid="send-test-email-btn"
                  >
                    <PaperPlaneTilt size={18} weight="duotone" className="mr-2" />
                    {sendingTest ? "Sending…" : "Send test email"}
                  </Button>
                </div>
              </div>
            </div>
          </div>

          <div className="grid sm:grid-cols-3 gap-5 mb-8">
            {[
              { icon: Medal, c: "#f59e0b", label: "Certificates earned", value: certCount },
              { icon: Student, c: "#0ea5e9", label: "Active learners", value: activeLearners },
              { icon: TrendUp, c: "#22c55e", label: "In-progress packages", value: completions.length },
            ].map((s, i) => (
              <div key={i} className="bg-white sticker rounded-3xl p-6 flex items-center gap-4">
                <span className="grid place-items-center w-12 h-12 rounded-2xl" style={{ backgroundColor: `${s.c}20` }}>
                  <s.icon size={26} weight="duotone" style={{ color: s.c }} />
                </span>
                <div>
                  <p className="font-body text-sm text-ink-soft">{s.label}</p>
                  <p className="font-heading font-bold text-2xl text-ink">{s.value}</p>
                </div>
              </div>
            ))}
          </div>

          <div className="bg-white sticker rounded-3xl overflow-hidden" data-testid="completions-table">
            {completions.length === 0 ? (
              <div className="p-12 text-center">
                <CertIcon size={40} weight="duotone" className="mx-auto text-brand-grape mb-3" />
                <p className="font-heading font-bold text-xl text-ink">No learner activity yet</p>
                <p className="font-body text-ink-soft mt-1">Once learners start lessons, their progress and certificates will appear here.</p>
              </div>
            ) : (
              <table className="w-full">
                <thead>
                  <tr className="border-b border-slate-900/10 font-body text-sm text-ink-soft">
                    <th className="text-left p-4">Learner</th>
                    <th className="text-left p-4 hidden sm:table-cell">Package</th>
                    <th className="text-left p-4">Progress</th>
                    <th className="text-left p-4">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {completions.map((c, i) => (
                    <tr key={`${c.user_id}-${c.package_id}`} className="border-b border-slate-900/5 last:border-0" data-testid={`completion-row-${i}`}>
                      <td className="p-4">
                        <div className="flex items-center gap-3">
                          {c.user_picture ? (
                            <img src={c.user_picture} alt="" className="w-9 h-9 rounded-full object-cover" />
                          ) : (
                            <span className="grid place-items-center w-9 h-9 rounded-full bg-brand-sky/15 text-brand-sky font-bold">{(c.user_name || "?")[0]}</span>
                          )}
                          <div>
                            <p className="font-heading font-semibold text-ink">{c.user_name}</p>
                            <p className="font-body text-sm text-ink-soft">{c.user_email}</p>
                          </div>
                        </div>
                      </td>
                      <td className="p-4 hidden sm:table-cell">
                        <p className="font-heading font-semibold text-ink">{c.package_title}</p>
                        <p className="font-body text-sm text-ink-soft">{c.level}</p>
                      </td>
                      <td className="p-4 min-w-[160px]">
                        <div className="flex items-center gap-2">
                          <div className="flex-1 h-2.5 rounded-full bg-slate-900/10 overflow-hidden">
                            <div className="h-full rounded-full" style={{ width: `${c.progress}%`, backgroundColor: c.color }} />
                          </div>
                          <span className="font-body text-sm font-semibold text-ink-soft whitespace-nowrap">{c.completed_count}/{c.lesson_count}</span>
                        </div>
                      </td>
                      <td className="p-4">
                        {c.completed ? (
                          <div className="flex items-center gap-2">
                            <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-bold bg-brand-grass/15 text-brand-grass" data-testid={`completion-status-${i}`}>
                              <Medal size={14} weight="fill" /> Certified
                            </span>
                            <Link to={`/admin/certificate/${c.user_id}/${c.package_id}`} data-testid={`view-certificate-${i}`}>
                              <Button variant="ghost" size="sm" className="rounded-full font-semibold text-brand-sky hover:bg-brand-sky/10 h-8 px-3">
                                <CertIcon size={16} weight="duotone" className="mr-1" /> View
                              </Button>
                            </Link>
                          </div>
                        ) : (
                          <span className="px-3 py-1 rounded-full text-xs font-bold bg-brand-sun/40 text-ink" data-testid={`completion-status-${i}`}>
                            {c.progress}% done
                          </span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </TabsContent>
      </Tabs>

      {/* Create / Edit dialog */}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-lg rounded-3xl max-h-[90vh] overflow-y-auto" data-testid="package-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading text-2xl">{editId ? "Edit package" : "New package"}</DialogTitle>
            <DialogDescription className="font-body">Fill in the reading package details below.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div>
              <Label className="font-body font-semibold">Title</Label>
              <Input className="rounded-xl mt-1" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} data-testid="form-title" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="font-body font-semibold">Level</Label>
                <Input className="rounded-xl mt-1" value={form.level} onChange={(e) => setForm({ ...form, level: e.target.value })} placeholder="Grade 1" data-testid="form-level" />
              </div>
              <div>
                <Label className="font-body font-semibold">Price ($)</Label>
                <Input type="number" className="rounded-xl mt-1" value={form.price} onChange={(e) => setForm({ ...form, price: e.target.value })} data-testid="form-price" />
              </div>
            </div>
            <div>
              <Label className="font-body font-semibold">Tagline</Label>
              <Input className="rounded-xl mt-1" value={form.tagline} onChange={(e) => setForm({ ...form, tagline: e.target.value })} data-testid="form-tagline" />
            </div>
            <div>
              <Label className="font-body font-semibold">Description</Label>
              <Textarea className="rounded-xl mt-1" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} data-testid="form-description" />
            </div>
            <div>
              <Label className="font-body font-semibold">Image URL</Label>
              <Input className="rounded-xl mt-1" value={form.image} onChange={(e) => setForm({ ...form, image: e.target.value })} data-testid="form-image" />
            </div>
            <div className="grid grid-cols-2 gap-3 items-end">
              <div>
                <Label className="font-body font-semibold">Accent color</Label>
                <Input type="color" className="rounded-xl mt-1 h-11 p-1" value={form.color} onChange={(e) => setForm({ ...form, color: e.target.value })} data-testid="form-color" />
              </div>
              <div className="flex items-center gap-3 pb-2">
                <Switch checked={form.featured} onCheckedChange={(v) => setForm({ ...form, featured: v })} data-testid="form-featured" />
                <Label className="font-body font-semibold">Featured</Label>
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" className="rounded-full" onClick={() => setOpen(false)}>Cancel</Button>
            <Button onClick={save} className="rounded-full bg-brand-grape hover:bg-brand-grape/90 text-white font-semibold" data-testid="save-package-btn">
              {editId ? "Save changes" : "Create package"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!deleteId} onOpenChange={(v) => !v && setDeleteId(null)}>
        <AlertDialogContent className="rounded-3xl" data-testid="delete-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-heading">Delete this package?</AlertDialogTitle>
            <AlertDialogDescription className="font-body">This will remove it from the catalog. This action cannot be undone.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="rounded-full">Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={doDelete} className="rounded-full bg-brand-coral hover:bg-brand-corallight" data-testid="confirm-delete-btn">Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
